/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import api from "../../utils/api";
import Swal from "sweetalert2";

const Checkout = () => {
  const { t } = useTranslation();
  const { responseId } = useParams();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const [paymentData, setPaymentData] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;

  const checkPaymentStatus = async () => {
    try {
      const response = await api.get(`/payments/status/${responseId}`);
      const data = response.data.data;

      setPaymentData(data);
      setRetryCount(0);

      if (data.is_paid) {
        setStatus("paid");
        Swal.fire({
          icon: "success",
          title: t("checkout_page.swal.success_title"),
          text: t("checkout_page.swal.success_text"),
          timer: 3000,
          showConfirmButton: false,
        }).then(() => {
          window.location.href = "/dashboard";
        });
      } else if (data.requires_payment === false) {
        setStatus("free");
      } else {
        setStatus("pending");
      }
    } catch (error) {
      console.error("Payment status error:", error);

      if (error.response?.status === 404 && retryCount < maxRetries) {
        setRetryCount((prev) => prev + 1);
        setTimeout(checkPaymentStatus, 3000);
      } else {
        setStatus("error");
        Swal.fire({
          icon: "error",
          title: t("checkout_page.swal.error_title"),
          text: t("checkout_page.swal.error_text"),
          confirmButtonText: t("checkout_page.swal.ok"),
        });
      }
    }
  };

  useEffect(() => {
    if (!responseId) {
      setStatus("error");
      Swal.fire(t("checkout_page.swal.invalid_title"), t("checkout_page.swal.invalid_text"), "error");
      return;
    }

    checkPaymentStatus();

    const interval = setInterval(() => {
      if (status === "pending" || status === "checking") {
        checkPaymentStatus();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [responseId, status, t]);

  if (status === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-700 font-medium">
            {t("checkout_page.checking_status")}
          </p>
          <p className="text-sm text-gray-500 mt-2">
            {t("checkout_page.response_id")} {responseId}
          </p>
        </div>
      </div>
    );
  }

  if (status === "free") {
    return (
      <div className="min-h-screen bg-gray-50 py-12 text-center">
        <div className="max-w-md mx-auto bg-white rounded-xl p-8 shadow-lg">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-10 h-10 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-green-600 mb-2">
            {t("checkout_page.free_title")}
          </h1>
          <p className="text-gray-600">
            {t("checkout_page.free_message")}
          </p>
          <button
            onClick={() => (window.location.href = "/dashboard")}
            className="mt-6 w-full bg-green-600 text-white py-2 rounded-lg font-medium hover:bg-green-700 transition"
          >
            {t("checkout_page.go_dashboard")}
          </button>
        </div>
      </div>
    );
  }

  if (status === "paid") {
    return (
      <div className="min-h-screen bg-gray-50 py-12 text-center">
        <div className="max-w-md mx-auto bg-white rounded-xl p-8 shadow-lg">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-10 h-10 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-green-600 mb-2">
            {t("checkout_page.confirmed_title")}
          </h1>
          <p className="text-gray-600">{t("checkout_page.redirecting")}</p>
        </div>
      </div>
    );
  }

  if (status === "pending") {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              {t("checkout_page.complete_payment")}
            </h1>
            <p className="text-gray-600 mb-2">
              {t("checkout_page.response_id")} <strong>{responseId}</strong>
            </p>
            <p className="text-3xl font-bold text-blue-600 mb-8">
              $
              {paymentData?.amount_paid || searchParams.get("amount") || "0.00"}
            </p>

            <div className="bg-gray-100 border-2 border-dashed rounded-xl p-12 mb-6">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-300 rounded w-3/4 mx-auto mb-3"></div>
                <div className="h-4 bg-gray-300 rounded w-1/2 mx-auto"></div>
              </div>
              <p className="text-gray-500 mt-4">
                {t("checkout_page.waiting_confirmation")}
              </p>
            </div>

            <div className="flex gap-3 justify-center">
              <button
                onClick={checkPaymentStatus}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition"
              >
                {t("checkout_page.refresh_status")}
              </button>
              <button
                onClick={() => (window.location.href = "/forms")}
                className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-400 transition"
              >
                {t("checkout_page.back_forms")}
              </button>
            </div>

            <p className="mt-6 text-xs text-gray-500">
              {t("checkout_page.secure_checkout")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 text-center">
      <div className="max-w-md mx-auto bg-white rounded-xl p-8 shadow-lg">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg
            className="w-10 h-10 text-red-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-red-600 mb-2">{t("checkout_page.error_title")}</h1>
        <p className="text-gray-600 mb-4">{t("checkout_page.error_message")}</p>
        <button
          onClick={() => (window.location.href = "/contact")}
          className="w-full bg-red-600 text-white py-2 rounded-lg font-medium hover:bg-red-700"
        >
          {t("checkout_page.contact_support")}
        </button>
      </div>
    </div>
  );
};

export default Checkout;
